<script setup>
import { thumbor } from './thumbor'
import { uuid } from '@/helpers'

const appStore = useAppStore()

const props = defineProps({
	// Component props
	defaultImg: {
		type: String,
		default: 'md',
	},
	sizes: {
		type: Array,
		default: () => [1, 2],
	},
	use: {
		type: Array,
		default: () => ['md'],
	},
	dimensions: {
		type: Object,
		default: () => {
			return {
				md: [0, 0],
			}
		},
	},
	classes: {
		type: Object,
		default: () => {},
	},
	img: {
		type: String,
		default: '',
		required: true,
	},
	placeholder: {
		type: String,
		default: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
	},
	smart: {
		type: Boolean,
		default: false,
	},
	fitIn: {
		type: Boolean,
		default: false,
	},
	filters: {
		type: String,
		default: '',
	},
	cropPreset: {
		type: String,
		default: '',
	},

	// Img specific props
	imgAlt: {
		type: String,
		default: '',
	},

	fluid: {
		type: Boolean,
		default: true,
	},
	loading: {
		type: [String, Boolean],
		default: 'lazy',
	},
})

const extension = computed(() => {
	return props.img.split('.').pop().toLowerCase()
})

const extensionMapper = {
	jpg: props.filters.includes('format(webp)') ? 'webp' : 'jpeg',
	jpeg: props.filters.includes('format(webp)') ? 'webp' : 'jpeg',
	png: props.filters.includes('format(webp)') ? 'webp' : 'png',
}

const source = (breakpoint, size = 1) => {
	const [width, height] = props.dimensions[breakpoint]

	const build = thumbor(props.img).dimension(width * size, height * size)

	if (props.cropPreset) {
		build.cropPreset(props.cropPreset)
	}

	if (props.filters) {
		build.filters(props.filters)
	}

	return build.url()
}

// Setting up
const sizes = props.use
	.map(breakpoint => {
		return {
			breakpoint,
			size: appStore.breakpoints[breakpoint],
		}
	})
	.sort((a, b) => b.size - a.size)

// console.log(sizes)

const sourceSets = computed(() => breakpoint => {
	// Do build magic here!
	if (!props.dimensions[breakpoint]) return

	let output = ''

	props.sizes.map((size, index) => {
		output += `${source(breakpoint, size)} ${size}x${index + 1 === props.sizes.length ? '' : ', '}`
	})

	return output
})

// const placeholder = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='

const figureId = uuid('fig-')
</script>

<template>
	<figure class="figure">
		<picture
			:class="[classes?.picture ?? '']"
			class="picture"
		>
			<source
				v-for="(size, breakpointIndex) in sizes"
				:key="`${figureId}-${size.breakpoint}`"
				:type="`image/${extensionMapper[extension]}`"
				:media="
					breakpointIndex + 1 === use.length || use.length === 1
						? `(max-width: ${size.size}px)`
						: breakpointIndex === 0
						? `(min-width: ${size.size}px)`
						: `(min-width: ${sizes[breakpointIndex + 1].size + 1}px) and (max-width: ${sizes[breakpointIndex - 1].size - 1}px)`
				"
				:srcset="sourceSets(size.breakpoint)"
				:width="props.dimensions[size.breakpoint][0]"
				:height="props.dimensions[size.breakpoint][1]"
			/>
			<slot>
				<img
					:alt="imgAlt ? imgAlt : ''"
					:src="source('md')"
					:class="[fluid ? 'img-fluid' : '', classes?.img ?? '']"
					:loading="loading ? loading : null"
					:width="props.dimensions.md[0]"
					:height="props.dimensions.md[1]"
					onerror="handleImgFallback(this);"
				/>
			</slot>
		</picture>
	</figure>
</template>
